// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — DecisionNode: Diamond
// 100% sizing pattern (same as ActivityNode)
// ═══════════════════════════════════════════════════════════════

import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, useReactFlow } from '@xyflow/react';
import { FLOW_TOKENS as T } from '../../shared/flowTokens';
import type { DecisionNodeData, FlowNodeData } from '../../types/flowchart.types';

const S = T.node.decisionSize;
const HALF = S / 2;

function DecisionNodeComponent({ id, data, selected }: NodeProps) {
  const d = data as unknown as DecisionNodeData;
  const { updateNodeData } = useReactFlow();
  const [editValue, setEditValue] = useState(d.label);
  const inputRef = useRef<HTMLInputElement>(null);
  const editing = !!(d as any).editing;

  useEffect(() => {
    if (editing) {
      setEditValue(d.label);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, d.label]);

  const saveEdit = useCallback(() => {
    updateNodeData(id, { label: editValue, editing: false } as Partial<FlowNodeData>);
  }, [id, editValue, updateNodeData]);

  const cancelEdit = useCallback(() => {
    updateNodeData(id, { editing: false } as Partial<FlowNodeData>);
  }, [id, updateNodeData]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter') saveEdit();
      if (e.key === 'Escape') cancelEdit();
    },
    [saveEdit, cancelEdit],
  );

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <NodeResizer
        minWidth={40}
        minHeight={40}
        isVisible={!!selected && !!(d as any).resizeEnabled}
        lineClassName="node-resizer-line"
        handleClassName="node-resizer-handle"
      />
      {/* 4 vertices — target + source en cada uno para recibir y enviar flechas */}
      <Handle
        type="target"
        position={Position.Top}
        id="top-t"
        style={{
          background: T.color.nodeStroke,
          width: 6,
          height: 6,
          border: 'none',
          left: '50%',
          top: 0,
          transform: 'translate(-50%, -50%)',
        }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-s"
        style={{
          background: 'transparent',
          width: 12,
          height: 12,
          border: 'none',
          left: '50%',
          top: 0,
          transform: 'translate(-50%, -50%)',
        }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-t"
        style={{
          background: T.color.nodeStroke,
          width: 6,
          height: 6,
          border: 'none',
          left: '50%',
          bottom: 0,
          transform: 'translate(-50%, 50%)',
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-s"
        style={{
          background: 'transparent',
          width: 12,
          height: 12,
          border: 'none',
          left: '50%',
          bottom: 0,
          transform: 'translate(-50%, 50%)',
        }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-t"
        style={{
          background: T.color.nodeStroke,
          width: 6,
          height: 6,
          border: 'none',
          right: 0,
          top: '50%',
          transform: 'translate(50%, -50%)',
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-s"
        style={{
          background: 'transparent',
          width: 12,
          height: 12,
          border: 'none',
          right: 0,
          top: '50%',
          transform: 'translate(50%, -50%)',
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-t"
        style={{
          background: T.color.nodeStroke,
          width: 6,
          height: 6,
          border: 'none',
          left: 0,
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-s"
        style={{
          background: 'transparent',
          width: 12,
          height: 12,
          border: 'none',
          left: 0,
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />

      <svg width="100%" height="100%" preserveAspectRatio="none" viewBox={`0 0 ${S} ${S}`}>
        <polygon
          points={`${HALF},1 ${S - 1},${HALF} ${HALF},${S - 1} 1,${HALF}`}
          fill={T.color.nodeFill}
          stroke={selected ? T.color.nodeStrokeSelected : T.color.nodeStroke}
          strokeWidth={selected ? T.shape.strokeWidthSelected : T.shape.strokeWidth}
        />
      </svg>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: T.font.family,
          pointerEvents: editing ? 'auto' : 'none',
        }}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={saveEdit}
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              fontFamily: T.font.family,
              color: T.color.textPrimary,
              textAlign: 'center',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '80%',
              padding: 0,
            }}
          />
        ) : (
          <span
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              color: T.color.textPrimary,
              textAlign: 'center',
              lineHeight: 1.1,
              padding: '0 4px',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
            }}
            title={d.label}
          >
            {d.label}
          </span>
        )}
      </div>
    </div>
  );
}

export const DecisionNode = memo(DecisionNodeComponent);
